import { useCartStore } from '../../../../../context/cart/CartStore';
import type {
  AddComponentDto,
  AddItemsDto,
  CartOrderLine,
  DeleteComponentDto,
  DeleteItemsDto,
} from '../../../../../../types/cart';

/**
 * Converts CartOrderLineComponent array to AddComponentDto array for re-adding to cart.
 */
const convertToAddComponents = (orderLine: CartOrderLine): AddComponentDto[] | undefined => {
  if (!orderLine.components || orderLine.components.length === 0) {
    return undefined;
  }

  return orderLine.components.map((component) => ({
    itemVariantId: component.itemVariantId,
    name: component.name,
    price: component.price,
    takeawayPrice: component.takeawayPrice,
    menuSectionId: component.menuSectionId,
    sectionProductId: component.sectionProductId,
    comment: component.comment,
    commentId: component.commentId,
    unitQuantity: component.unitQuantity,
    courseId: component.courseId,
    options: component.options.map((option) => ({
      itemVariantId: option.itemVariantId,
      name: option.name,
      price: option.price,
      quantity: option.quantity,
      takeawayPrice: option.takeawayPrice,
      type: option.type,
    })),
    // Recursively convert nested components
    components: component.components
      ? component.components.map((nested) => ({
          comment: nested.comment,
          commentId: nested.commentId,
          courseId: nested.courseId,
          itemVariantId: nested.itemVariantId,
          menuSectionId: nested.menuSectionId,
          name: nested.name,
          options: nested.options.map((opt) => ({
            itemVariantId: opt.itemVariantId,
            name: opt.name,
            price: opt.price,
            takeawayPrice: opt.takeawayPrice,
            type: opt.type,
            quantity: opt.quantity,
          })),
          price: nested.price,
          sectionProductId: nested.sectionProductId,
          takeawayPrice: nested.takeawayPrice,
          unitQuantity: nested.unitQuantity,
        }))
      : undefined,
  }));
};

/**
 * Converts CartOrderLineComponent array to DeleteComponentDto array for deletion matching.
 */
const convertToDeleteComponents = (orderLine: CartOrderLine): DeleteComponentDto[] | undefined => {
  if (!orderLine.components || orderLine.components.length === 0) {
    return undefined;
  }

  return orderLine.components.map((component) => ({
    itemVariantId: component.itemVariantId,
    name: component.name,
    price: component.price,
    takeawayPrice: component.takeawayPrice,
    menuSectionId: component.menuSectionId,
    sectionProductId: component.sectionProductId,
    comment: component.comment,
    commentId: component.commentId,
    unitQuantity: component.unitQuantity,
    courseId: component.courseId,
    options: component.options.map((option) => ({
      itemVariantId: option.itemVariantId,
      name: option.name,
      price: option.price,
      quantity: option.quantity,
      takeawayPrice: option.takeawayPrice,
      type: option.type,
    })),
    // Recursively convert nested components
    components: component.components
      ? component.components.map((nested) => ({
          comment: nested.comment,
          commentId: nested.commentId,
          courseId: nested.courseId,
          itemVariantId: nested.itemVariantId,
          menuSectionId: nested.menuSectionId,
          name: nested.name,
          options: nested.options.map((opt) => ({
            itemVariantId: opt.itemVariantId,
            name: opt.name,
            price: opt.price,
            takeawayPrice: opt.takeawayPrice,
            type: opt.type,
            quantity: opt.quantity,
          })),
          price: nested.price,
          sectionProductId: nested.sectionProductId,
          takeawayPrice: nested.takeawayPrice,
          unitQuantity: nested.unitQuantity,
        }))
      : undefined,
  }));
};

export const useUpdateCartItems = () => {
  const { addMenuItems, deleteMenuItems } = useCartStore((state) => ({
    addMenuItems: state.addMenuItems,
    deleteMenuItems: state.deleteMenuItems,
  }));

  return (incrementOrDecrement: number, orderLine: CartOrderLine) => {
    const item = {
      comment: orderLine.comment,
      commentId: orderLine.commentId,
      courseId: orderLine.courseId,
      giftCardId: orderLine.giftCardId,
      itemVariantId: orderLine.itemVariantId,
      menuSectionId: orderLine.menuSectionId,
      name: orderLine.name,
      openPrice: orderLine.openPrice,
      options: orderLine.options.map((option) => {
        return {
          itemVariantId: option.itemVariantId,
          name: option.name,
          price: option.price,
          takeawayPrice: option.takeawayPrice,
          type: option.type,
          quantity: option.quantity,
        };
      }),
      price: orderLine.price,
      sectionProductId: orderLine.sectionProductId,
      takeawayPrice: orderLine.takeawayPrice,
    };

    if (incrementOrDecrement > 0) {
      const addItem: AddItemsDto = {
        item: {
          ...item,
          components: convertToAddComponents(orderLine),
        },
        quantity: incrementOrDecrement,
      };
      addMenuItems(addItem);
      return;
    }

    const deleteItem: DeleteItemsDto = {
      item: {
        ...item,
        components: convertToDeleteComponents(orderLine),
      },
      quantity: incrementOrDecrement * -1,
    };
    deleteMenuItems(deleteItem);
  };
};
